import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
        
        <div className="space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">Last Updated: {new Date().toLocaleDateString()}</p>
          
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
            <p>
              Vinculum ("we", "our", or "us") operates the I-Match CRM application. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our service.
              We are committed to protecting your privacy and complying with the General Data Protection 
              Regulation (GDPR) and other applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Data Controller</h2>
            <p className="mb-2">
              <strong>Company Name:</strong> Vinculum<br />
              <strong>Data Protection Officer:</strong> Kshitij Shah<br />
              <strong>Contact:</strong> For privacy-related inquiries, please contact our DPO
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-2 mt-4">3.1 Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Information:</strong> Email address, first name, last name, password (encrypted)</li>
              <li><strong>Profile Information:</strong> User role, account status, preferences</li>
              <li><strong>Contact Data:</strong> Information you add about leads, companies, and contacts</li>
              <li><strong>Usage Data:</strong> Login times, features used, actions performed</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-4">3.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>IP address and session information</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Usage patterns and preferences</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-4">3.3 Cookies and Tracking</h3>
            <p>
              We use essential cookies for authentication and session management. We plan to implement 
              Google Analytics for usage statistics. You can control cookie preferences through your 
              browser settings or our cookie consent banner.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Legal Basis for Processing</h2>
            <p className="mb-2">Under GDPR, we process your data based on:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Contract Performance:</strong> To provide CRM services you've signed up for</li>
              <li><strong>Legitimate Interest:</strong> To improve our services and prevent fraud</li>
              <li><strong>Consent:</strong> For marketing communications and analytics (where required)</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, operate, and maintain our CRM services</li>
              <li>Authenticate users and manage accounts</li>
              <li>Process and enrich lead data</li>
              <li>Send administrative information and updates</li>
              <li>Respond to support requests</li>
              <li>Improve our services and develop new features</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Your GDPR Rights</h2>
            <p className="mb-2">You have the following rights under GDPR:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
              <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Right to Object:</strong> Object to certain processing activities</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
              <li><strong>Right to Lodge a Complaint:</strong> File a complaint with supervisory authority</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, visit your GDPR Settings page or contact our DPO.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Data Retention</h2>
            <p>
              We retain your personal data for as long as necessary to provide our services and comply 
              with legal obligations. When you request account deletion, we:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Immediately deactivate your account</li>
              <li>Retain data for 1 year for legal and backup purposes</li>
              <li>Permanently anonymize data after the retention period</li>
              <li>You can cancel the deletion request during the 1-year period</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your data:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Encryption of passwords and sensitive data</li>
              <li>Secure HTTPS connections</li>
              <li>Access controls and authentication</li>
              <li>Regular security audits</li>
              <li>Audit logs for data access and modifications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Data Sharing and Transfers</h2>
            <p>
              We do not sell your personal data. We may share data with:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Service Providers:</strong> Third-party services that help operate our platform</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect rights</li>
              <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale</li>
            </ul>
            <p className="mt-2">
              Data may be transferred outside the EU. We ensure adequate safeguards are in place.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. Children's Privacy</h2>
            <p>
              Our service is not intended for users under 16 years of age. We do not knowingly collect 
              personal information from children under 16.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant 
              changes by email or through our service. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">12. Contact Us</h2>
            <p>
              For questions about this Privacy Policy or to exercise your rights, please contact:
            </p>
            <div className="mt-2 p-4 bg-gray-50 rounded-lg">
              <p><strong>Vinculum</strong></p>
              <p>Data Protection Officer: Kshitij Shah</p>
              <p className="mt-2">You can also manage your data through the GDPR Settings in your user profile.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
